<script setup>
import { computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { uiSettings } = useUISettings();

const notes = computed(() => store.getters['accountNotes/getAllNotes']);
const meta = computed(() => store.getters['accountNotes/getMeta']);
const uiFlags = computed(() => store.getters['accountNotes/getUIFlags']);
const currentChat = computed(() => store.getters.getSelectedChat);

const isOnExpandedLayout = computed(() => {
  const {
    LAYOUT_TYPES: { CONDENSED },
  } = wootConstants;
  const { conversation_display_type: type = CONDENSED } = uiSettings.value;
  return type !== CONDENSED;
});

const shouldShowSidebar = computed(() => {
  if (!currentChat.value?.id) return false;
  const {
    is_contact_sidebar_open: isContactSidebarOpen,
    is_templates_panel_open: isTemplatesPanelOpen,
  } = uiSettings.value;
  return isContactSidebarOpen || isTemplatesPanelOpen;
});

const selectedConversationId = computed(() =>
  Number(route.query.conversationId ?? 0)
);

const currentPage = computed(() => Number(route.query.page ?? 1));

const fetchNotes = page => {
  store.dispatch('accountNotes/fetch', page);
};

const onPageChange = page => {
  router.push({ query: { page } });
};

const loadConversation = async conversationId => {
  if (!conversationId) {
    store.dispatch('clearSelectedState');
    return;
  }
  const existing = store.getters.getConversationById(conversationId);
  if (!existing) {
    await store.dispatch('getConversation', conversationId);
  }
  const conversation =
    store.getters.getConversationById(conversationId) ||
    store.getters.getAllConversations.find(c => c.id === conversationId);
  if (conversation) {
    store.dispatch('setActiveChat', { data: conversation }).then(() => {
      emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
    });
  }
};

const onNoteClick = note => {
  const conversationId = note.contact?.latest_conversation_id;
  if (!conversationId) return;
  router.push({ query: { ...route.query, conversationId } });
};

onMounted(() => {
  fetchNotes(currentPage.value);
  if (selectedConversationId.value) {
    loadConversation(selectedConversationId.value);
  } else {
    store.dispatch('clearSelectedState');
  }
});

onUnmounted(() => {
  store.dispatch('clearSelectedState');
});

watch(currentPage, (newPage, oldPage) => {
  if (newPage !== oldPage) fetchNotes(newPage);
});

watch(selectedConversationId, conversationId => {
  loadConversation(conversationId);
});

const totalPages = computed(() =>
  Math.ceil(meta.value.totalCount / meta.value.perPage)
);
</script>

<template>
  <section class="flex w-full h-full min-w-0">
    <!-- Left panel: notes list -->
    <div
      class="flex flex-col h-full border-r border-n-weak bg-n-background overflow-hidden w-[340px] min-w-[280px] max-w-[380px] flex-shrink-0"
    >
      <!-- Header -->
      <div class="flex items-center px-4 py-3 border-b border-n-weak">
        <h2 class="text-heading-3 text-n-slate-12 font-semibold">
          {{ t('CONTACT_NOTES_VIEW.TITLE') }}
        </h2>
        <span class="ml-2 text-sm text-n-slate-10">
          ({{ meta.totalCount }})
        </span>
      </div>

      <!-- Loading -->
      <div
        v-if="uiFlags.isFetching"
        class="flex items-center justify-center flex-1"
      >
        <span class="text-n-slate-10 text-body-main">
          {{ t('CONTACT_NOTES_VIEW.LOADING') }}
        </span>
      </div>

      <!-- Empty -->
      <div
        v-else-if="!notes.length"
        class="flex items-center justify-center flex-1"
      >
        <span class="text-n-slate-10 text-body-main">
          {{ t('CONTACT_NOTES_VIEW.EMPTY') }}
        </span>
      </div>

      <!-- Notes list -->
      <div v-else class="flex flex-col overflow-y-auto flex-1">
        <div
          v-for="note in notes"
          :key="note.id"
          class="flex flex-col gap-2 px-4 py-3 border-b border-n-weak transition-colors"
          :class="
            note.contact?.latest_conversation_id
              ? 'cursor-pointer hover:bg-n-slate-2'
              : 'cursor-default'
          "
          :style="
            note.contact?.latest_conversation_id &&
            selectedConversationId === note.contact.latest_conversation_id
              ? 'background: var(--color-woot-25, rgba(var(--color-woot-rgb), 0.06))'
              : ''
          "
          @click="onNoteClick(note)"
        >
          <!-- Contact + date -->
          <div class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-1.5 min-w-0">
              <span
                class="i-lucide-user text-n-slate-9 size-3.5 flex-shrink-0"
              />
              <span class="text-xs font-semibold text-n-slate-12 truncate">
                {{
                  note.contact?.name || t('CONTACT_NOTES_VIEW.UNKNOWN_CONTACT')
                }}
              </span>
              <span
                v-if="note.contact?.phone_number"
                class="text-xs text-n-slate-9 truncate"
              >
                {{ note.contact.phone_number }}
              </span>
            </div>
            <div class="flex items-center gap-1.5 flex-shrink-0">
              <span
                v-if="note.contact?.latest_conversation_id"
                class="i-lucide-message-circle text-n-slate-7 size-3"
              />
              <span class="text-xs text-n-slate-9 whitespace-nowrap">
                {{ messageTimestamp(note.created_at, 'dd/MM HH:mm') }}
              </span>
            </div>
          </div>

          <!-- Content -->
          <p
            class="text-sm text-n-slate-12 whitespace-pre-wrap leading-relaxed line-clamp-3"
          >
            {{ note.content }}
          </p>

          <!-- Author -->
          <div class="flex items-center gap-1 text-xs text-n-slate-9">
            <span class="i-lucide-pencil size-3" />
            <span>
              {{
                note.user ? note.user.name : t('CONTACT_NOTES_VIEW.SYSTEM_USER')
              }}
            </span>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div
        v-if="totalPages > 1"
        class="flex items-center justify-center gap-2 px-4 py-3 border-t border-n-weak"
      >
        <button
          :disabled="currentPage <= 1"
          class="px-3 py-1 text-xs border border-n-slate-6 rounded text-n-slate-11 hover:bg-n-slate-3 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          @click="onPageChange(currentPage - 1)"
        >
          {{ t('CONTACT_NOTES_VIEW.PREV') }}
        </button>
        <span class="text-xs text-n-slate-10">
          {{ currentPage }} {{ t('CONTACT_NOTES_VIEW.PAGE_OF') }}
          {{ totalPages }}
        </span>
        <button
          :disabled="currentPage >= totalPages"
          class="px-3 py-1 text-xs border border-n-slate-6 rounded text-n-slate-11 hover:bg-n-slate-3 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          @click="onPageChange(currentPage + 1)"
        >
          {{ t('CONTACT_NOTES_VIEW.NEXT') }}
        </button>
      </div>
    </div>

    <!-- Right panel: conversation -->
    <ConversationBox :is-on-expanded-layout="isOnExpandedLayout">
      <SidepanelSwitch v-if="currentChat?.id" />
    </ConversationBox>
    <ConversationSidebar v-if="shouldShowSidebar" :current-chat="currentChat" />
  </section>
</template>
